import React, {useState, useRef, useEffect} from 'react'

import {FieldValues, useForm} from 'react-hook-form'
import {useRouter} from 'next/router'
import {useUTMContext} from '@lib/gtm'
import {useAsync} from '@lib/hooks'
import {getIp, sendEmail} from '@lib/services'
import {acceptConsent} from '@lib/utils/consent'
import {zodResolver} from '@hookform/resolvers/zod'
import {InquiryEmailSchema} from '../../schema/EmailSchema'

import Checkbox from '../Checkbox'
import {FormInput, FormTextArea} from '../custom'
import {logEvent} from '@lib/gtm'

type FormProps = {
  title: string
}

export const ContactForm = ({title}: FormProps) => {
  const {run: runSendEmail, isLoading, isSuccess} = useAsync(sendEmail)
  const {data: utm} = useUTMContext()
  const [isComplete, setIsComplete] = useState(false)
  const router = useRouter()
  const {run: runGetIp} = useAsync(getIp)

  const {
    register,
    handleSubmit,
    formState: {errors},
    setValue,
    getValues,
    reset,
    watch,
  } = useForm<FieldValues>({
    defaultValues: {
      isAccept: false,
      isAcceptNews: true,
    },
    resolver: zodResolver(InquiryEmailSchema),
  })
  const watchFields = watch(['isAccept', 'isAcceptNews'])

  const ref = useRef()

  const onSubmit = async (data) => {
    const ip = await runGetIp({})
    const emailData = {
      to: process.env.SALES_EMAIL,
      subject: `[${window.location.host}] ${data.email} wants to contact with you`,
      message: `
        Name: ${data.name}
        Email: ${data.email}
        Tel: (+66) ${data.phone}
        Company: ${data.company}
        Position: ${data.position}
        Description : ${data.message}
        IP Address: ${ip}
      `,
    }

    const reqdata = {
      name: data.name,
      company: data.company,
      position: data.position,
      email: data.email,
      phone: data.phone,
      message: data.message,
      template: 'consult',
      url: window.location.href,
    }

    const res = await runSendEmail({
      emailData: {...emailData, path: process.env.EMAIL_API_URL},
      reqBody: reqdata,
      utm: utm,
    })

    if (res?.status === 200 && data?.isAcceptNews) {
      await acceptConsent({
        apiKey: process.env.CONSENT_WOW_CONTACT_API_KEY,
        purposeId: process.env.CONSENT_WOW_MARKETING_CONSENT_PURPOSE_ID,
        data: {
          ...data,
          name: data.name,
          surname: data.surname,
          phone_number: data.phone_number,
        },
      })
    }
  }

  React.useEffect(() => {
    if (isSuccess) {
      reset()
      setIsComplete(isSuccess)

      logEvent({
        ga: {
          category: 'Submit Form Success',
          action: 'Submit Form Success',
          label: router.pathname.replace('/', '').replaceAll('-', ' '),
        },
        fb: {
          event: 'Lead',
        },
      })
    }
  }, [isSuccess])

  useEffect(() => {
    if (isComplete) {
      setTimeout(() => {
        setIsComplete(false)
      }, 5000)
    }
  }, [isComplete])

  return (
    <div className='blog-contact'>
      <h4 className='title'>{title}</h4>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className='blog-form'
        id='contact-form'
        ref={ref}
      >
        <div className='fields'>
          <FormInput
            name='name'
            label='ชื่อ'
            placeholder='ชื่อ'
            register={register}
            zodRequired={true}
            error={errors.name?.message ? String(errors.name?.message) : ''}
          />
          <FormInput
            name='company'
            label='ชื่อบริษัท'
            placeholder='ชื่อบริษัท'
            register={register}
            zodRequired={true}
            error={
              errors.company?.message ? String(errors.company?.message) : ''
            }
          />
          <FormInput
            name='position'
            label='ชื่อตำแหน่ง'
            placeholder='ชื่อตำแหน่ง'
            register={register}
          />
          <FormInput
            name='phone'
            label='เบอร์โทรศัพท์'
            type='number'
            placeholder='เบอร์โทรศัพท์'
            register={register}
            zodRequired={true}
            error={errors.phone?.message ? String(errors.phone?.message) : ''}
          />
          <FormInput
            name='email'
            type='email'
            label='อีเมล'
            placeholder='อีเมล'
            register={register}
            zodRequired={true}
            error={errors.email?.message ? String(errors.email?.message) : ''}
          />
        </div>
        <FormTextArea
          name='message'
          label='ข้อความ'
          placeholder='ข้อความ'
          register={register}
        />
        <div className='condition'>
          <Checkbox
            name='accept'
            className='checkbox'
            isChecked={
              getValues('isAcceptNews') === undefined
                ? true
                : getValues('isAcceptNews')
            }
            onClick={() => {
              setValue('isAcceptNews', !getValues('isAcceptNews'))
            }}
          >
            ฉันต้องการรับโปรโมชันและข่าวสารทางการตลาดเกี่ยวกับ Data Wow
            และบริการอื่น ๆ จากเรา บริษัทในเครือ บริษัทย่อยและพันธมิตรทางธุรกิจ
            &#8203;
            <span style={{whiteSpace: 'nowrap'}}>
              (คุณสามารถยกเลิกได้ทุกเมื่อ)
            </span>
          </Checkbox>
          <Checkbox
            className='checkbox'
            required
            isChecked={getValues('isAccept')}
            onClick={() => {
              setValue('isAccept', !getValues('isAccept'))
            }}
          >
            ฉันยอมรับเงื่อนไข{' '}
            <a
              href='/terms-of-use'
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => e.stopPropagation()}
            >
              เงื่อนไขการให้บริการ{' '}
            </a>
            และ
            <a
              href='/policy'
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => e.stopPropagation()}
            >
              นโยบายความเป็นส่วนตัว
            </a>
          </Checkbox>
        </div>
        <button
          type='submit'
          disabled={isLoading || !watchFields[0]}
          className='form-button'
        >
          {isComplete
            ? 'ดำเนินการเรียบร้อย'
            : isLoading
            ? 'กำลังดำเนินการ...'
            : 'ส่งข้อมูล'}
        </button>
      </form>
    </div>
  )
}
